import { useMutation, useQuery } from "@apollo/client";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FaFilter, FaUndo } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import TableSkeletonLoader from "../../../common/TableLoading";
import ConformationModal from "../../../components/ConformationModal";
import Pagination from "../../../components/Pagination";
import AppButton from "../../../shared/AppButton";
import AppTable from "../../../shared/AppTable";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import icdCodes from "../../../gql/icdCodes";

const emptyForm = {
  code_id: "",
  ascii_description: "",
  gender: "both",
  is_secondary: "false",
  req_morphology: "false",
  status: "active",
};

const statusOptions = [
  { id: "active", name: "Active" },
  { id: "inactive", name: "Inactive" },
];

const genderOptions = [
  { id: "M", name: "M" },
  { id: "F", name: "F" },
  { id: "both", name: "Both" },
];

const secondaryOptions = [
  { id: "true", name: "Yes" },
  { id: "false", name: "No" },
];

function IcdCodesList() {
  const { t } = useTranslation();
  const [limit] = useState(15);
  const [pageNo, setPageNo] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [statusTerm, setStatusTerm] = useState("active");
  const [genderTerm, setGenderTerm] = useState("");
  const [secondaryTerm, setSecondaryTerm] = useState("");
  const [status, setStatus] = useState("active");
  const [gender, setGender] = useState("");
  const [secondary, setSecondary] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCodeId, setEditingCodeId] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const variables = useMemo(
    () => ({
      page: pageNo,
      limit,
      search,
      status: status || null,
      gender: gender || null,
      isSecondary: secondary,
    }),
    [gender, limit, pageNo, search, secondary, status]
  );

  const { loading, error, data, refetch } = useQuery(icdCodes.GET_ICD_CODES, {
    variables,
    fetchPolicy: "network-only",
  });

  const [createIcdCode, { loading: creating }] = useMutation(
    icdCodes.CREATE_ICD_CODE,
    {
      onCompleted: () => {
        toast.success(t("ICD code created"));
        setFormOpen(false);
        refetch();
      },
      onError: (err) => toast.error(err.message),
    }
  );

  const [updateIcdCode, { loading: updating }] = useMutation(
    icdCodes.UPDATE_ICD_CODE,
    {
      onCompleted: () => {
        toast.success(t("ICD code updated"));
        setFormOpen(false);
        refetch();
      },
      onError: (err) => toast.error(err.message),
    }
  );

  const [deleteIcdCode] = useMutation(icdCodes.DELETE_ICD_CODE, {
    onCompleted: () => {
      toast.success(t("ICD code deactivated"));
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  const rows = data?.getIcdCodes10?.codes || [];
  const pageInfo = data?.getIcdCodes10?.pageInfo;

  const validationSchema = Yup.object().shape({
    code_id: Yup.string().trim().required(t("ICD Code ID is required")),
    ascii_description: Yup.string()
      .trim()
      .required(t("ASCII Description is required")),
    gender: Yup.string().oneOf(["M", "F", "both"]).required(t("Gender is required")),
    is_secondary: Yup.string()
      .oneOf(["true", "false"])
      .required(t("Secondary Diagnosis is required")),
    req_morphology: Yup.string()
      .oneOf(["true", "false"])
      .required(t("Requires Morphology is required")),
    status: Yup.string()
      .oneOf(["active", "inactive"])
      .required(t("Status is required")),
  });

  const formik = useFormik({
    initialValues: emptyForm,
    validationSchema,
    onSubmit: (values) => {
      const input = {
        code_id: values.code_id.trim().toUpperCase(),
        ascii_description: values.ascii_description.trim(),
        gender: values.gender || null,
        is_secondary: values.is_secondary === "true",
        req_morphology: values.req_morphology === "true",
        status: values.status || "active",
      };

      if (editingCodeId) {
        updateIcdCode({ variables: { codeId: editingCodeId, input } });
        return;
      }

      createIcdCode({ variables: { input } });
    },
  });

  const openCreate = () => {
    setEditingCodeId(null);
    formik.resetForm({ values: emptyForm });
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingCodeId(item.code_id);
    formik.setValues({
      code_id: item.code_id || "",
      ascii_description: item.ascii_description || "",
      gender: item.gender || "both",
      is_secondary: item.is_secondary ? "true" : "false",
      req_morphology: item.req_morphology ? "true" : "false",
      status: item.status || "active",
    });
    setFormOpen(true);
  };

  const onClickDelete = (item) => {
    setCurrentItem(item);
    setShowDeleteAlert(true);
  };

  const handleDelete = () => {
    deleteIcdCode({ variables: { codeId: currentItem.code_id } });
    setShowDeleteAlert(false);
  };

  const columns = [
    {
      header: t("Code"),
      field: "code_id",
      render: (row) => <span className="font-medium">{row.code_id}</span>,
    },
    { header: t("Description"), field: "ascii_description" },
    {
      header: t("Gender"),
      render: (row) => row.gender || "-",
    },
    {
      header: t("Secondary"),
      render: (row) => (row.is_secondary ? t("Yes") : t("No")),
    },
    {
      header: t("Morphology"),
      render: (row) => (row.req_morphology ? t("Required") : t("No")),
    },
    {
      header: t("Status"),
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            row.status === "active"
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {t(row.status || "-")}
        </span>
      ),
    },
  ];

  return (
    <div className="superGit_container !p-0">
      <div className="flex justify-between items-start flex-col md:flex-row gap-2 mb-2">
        <div className="w-full sm:w-[calc(100%-170px)]">
          <span className="font-semibold text-black/70 text-lg">
            {t("ICD CODES")}
            <span className="text-lg ml-1">
              ({pageInfo?.totalCount || 0}{" "}
              <span className="text-xs font-bold italic">{t("Entries")}</span>)
            </span>
          </span>
        </div>
        <AppButton
          label={t("Create ICD Code")}
          startIcon={<AddIcon />}
          onClick={openCreate}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
        <div className="w-full flex flex-col lg:flex-row gap-x-1.5 gap-y-1.5 justify-end">
          <div className="w-full lg:w-[260px]">
            <CustomFilterInput
              label={t("Search ICD Codes")}
              name="search"
              placeholder={t("Search by code or description")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="w-full lg:w-[150px]">
            <CustomFilterInput
              label={t("Status")}
              name="status"
              placeholder="All"
              isSelect
              options={statusOptions}
              value={statusTerm}
              onChange={(e) => setStatusTerm(e.target.value)}
            />
          </div>
          <div className="w-full lg:w-[140px]">
            <CustomFilterInput
              label={t("Gender")}
              name="gender"
              placeholder="All"
              isSelect
              options={genderOptions}
              value={genderTerm}
              onChange={(e) => setGenderTerm(e.target.value)}
            />
          </div>
          <div className="w-full lg:w-[150px]">
            <CustomFilterInput
              label={t("Secondary")}
              name="secondary"
              placeholder="All"
              isSelect
              options={secondaryOptions}
              value={secondaryTerm}
              onChange={(e) => setSecondaryTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2 mt-[19px]">
            <AppButton
              variant="reset"
              className="!bg-slate-200"
              onClick={() => {
                setSearchTerm("");
                setSearch("");
                setStatusTerm("active");
                setGenderTerm("");
                setSecondaryTerm("");
                setStatus("active");
                setGender("");
                setSecondary(null);
                setPageNo(1);
              }}
              startIcon={<FaUndo className="!text-[12px]" />}
            />
            <AppButton
              onClick={() => {
                setSearch(searchTerm.trim());
                setStatus(statusTerm);
                setGender(genderTerm);
                setSecondary(secondaryTerm === "" ? null : secondaryTerm === "true");
                setPageNo(1);
              }}
              startIcon={<FaFilter className="!text-[12px]" />}
            />
          </div>
        </div>
      </div>

      <AppTable
        columns={columns}
        data={rows}
        loading={loading}
        onEdit={openEdit}
        onDelete={onClickDelete}
        LoaderComponent={<TableSkeletonLoader columnCount={6} rowCount={10} />}
      />

      <div className="mt-4">
        <Pagination
          currentPage={pageNo}
          totalPages={pageInfo?.totalPages || 1}
          onPageChange={setPageNo}
        />
      </div>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingCodeId ? t("Edit ICD Code") : t("Create ICD Code")}
        </DialogTitle>
        <DialogContent className="!pt-2">
          <form
            id="icd-code-form"
            onSubmit={formik.handleSubmit}
            className="space-y-3"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CustomFilterInput
                label={t("ICD Code ID")}
                name="code_id"
                placeholder={t("Enter ICD code")}
                value={formik.values.code_id}
                disabled={Boolean(editingCodeId)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.code_id}
                touched={formik.touched.code_id}
              />
              <CustomFilterInput
                label={t("Status")}
                name="status"
                placeholder={t("Select status")}
                isSelect
                options={statusOptions}
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.status}
                touched={formik.touched.status}
              />
            </div>
            <div>
              <CustomFilterInput
                label={t("ASCII Description")}
                name="ascii_description"
                type="textarea"
                placeholder={t("Enter ICD description")}
                value={formik.values.ascii_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.ascii_description}
                touched={formik.touched.ascii_description}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <CustomFilterInput
                label={t("Gender")}
                name="gender"
                placeholder={t("Select gender")}
                isSelect
                options={genderOptions}
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.gender}
                touched={formik.touched.gender}
              />
              <CustomFilterInput
                label={t("Secondary Diagnosis")}
                name="is_secondary"
                placeholder={t("Select")}
                isSelect
                options={secondaryOptions}
                value={formik.values.is_secondary}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.is_secondary}
                touched={formik.touched.is_secondary}
              />
              <CustomFilterInput
                label={t("Requires Morphology")}
                name="req_morphology"
                placeholder={t("Select")}
                isSelect
                options={secondaryOptions}
                value={formik.values.req_morphology}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.req_morphology}
                touched={formik.touched.req_morphology}
              />
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <AppButton
            variant="reset"
            label={t("Cancel")}
            onClick={() => setFormOpen(false)}
          />
          <AppButton
            label={editingCodeId ? t("Update") : t("Create")}
            type="submit"
            form="icd-code-form"
            isLoading={creating || updating}
            loadingText={editingCodeId ? t("Updating...") : t("Creating...")}
            disabled={creating || updating}
          />
        </DialogActions>
      </Dialog>

      <ConformationModal
        open={showDeleteAlert}
        setOpen={setShowDeleteAlert}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default IcdCodesList;
